'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Shield, Lock, Eye, FileText, Users, GitBranch, AlertCircle } from 'lucide-react';

export default function GDPRCompliancePage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const sections = [
    {
      icon: Shield,
      title: 'Our Commitment to GDPR',
      content:
        'PlotsAlot is fully GDPR compliant. We are committed to protecting the personal data of all individuals in the European Union, United Kingdom, and other regions covered by the GDPR. Our policies and practices ensure full compliance with the General Data Protection Regulation (GDPR) and other applicable data protection laws.',
    },
    {
      icon: Lock,
      title: 'Your Data Rights',
      content:
        'As a data subject, you have specific rights under GDPR. You can access your personal data, request corrections to inaccurate information, request deletion of your data (right to be forgotten), request data portability, restrict processing, and object to certain processing activities. To exercise any of these rights, please contact us at support@slashml.com.',
    },
    {
      icon: Eye,
      title: 'Data Processing',
      content:
        'We transparently process personal data only for specified, explicit, and legitimate purposes. We use appropriate technical and organizational measures to ensure the security of your data. We maintain data for only as long as necessary to fulfill the purposes for which it was collected.',
    },
    {
      icon: Users,
      title: 'Our Role in Data Processing',
      content:
        'PlotsAlot primarily acts as a data processor on behalf of our customers, who serve as controllers. This means our customers determine what personal data is shared with us. When we collect data directly from visitors to our website, we act as a controller. In certain cases where we aggregate data for analytics purposes, we may act as both processor and controller.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/50 via-purple-100/30 to-background dark:from-purple-900/20 dark:via-purple-900/5 dark:to-background -z-10"></div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-64 h-64 rounded-full bg-purple-200/20 dark:bg-purple-800/20 blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-[10%] w-96 h-96 rounded-full bg-indigo-200/20 dark:bg-indigo-800/20 blur-3xl -z-10"></div>

      <Navbar />

      <main className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center">
            <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">GDPR Compliance</h1>
            <p className="text-lg text-muted-foreground">
              Your privacy and data protection are our top priorities
            </p>
          </motion.div>

          {/* Main content */}
          <motion.div
            className="space-y-12 bg-card/50 backdrop-blur-sm rounded-xl p-8 border border-border shadow-xl mb-12"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}>
            {/* Key sections */}
            <div className="grid md:grid-cols-2 gap-8">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    variants={fadeIn}
                    className="p-6 rounded-lg bg-gradient-to-br from-transparent to-blue-50/50 dark:to-blue-950/20 border border-blue-200/50 dark:border-blue-900/50">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-semibold text-foreground mb-2">{section.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Data Subject Rights */}
            <motion.div variants={fadeIn} className="mt-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">Data Subject Rights Under GDPR</h2>
              <div className="space-y-4">
                {[
                  {
                    title: 'Right to Access',
                    desc: 'You have the right to obtain confirmation as to whether your personal data is being processed, and to view the personal data we hold about you.',
                  },
                  {
                    title: 'Right to Correction',
                    desc: 'You can request correction of inaccurate or incomplete personal data held about you.',
                  },
                  {
                    title: 'Right to Erasure',
                    desc: 'You can request deletion of your personal data under certain circumstances (right to be forgotten).',
                  },
                  {
                    title: 'Right to Data Portability',
                    desc: 'You have the right to receive your personal data in a structured, commonly used, and machine-readable format, and to transmit that data to another processor.',
                  },
                  {
                    title: 'Right to Restrict Processing',
                    desc: 'You can request restriction of processing of your personal data in specific situations.',
                  },
                  {
                    title: 'Right to Object',
                    desc: 'You have the right to object to certain processing activities, including processing for direct marketing purposes.',
                  },
                  {
                    title: 'Right to Withdraw Consent',
                    desc: 'Where processing is based on your consent, you can withdraw that consent at any time. Withdrawal of consent will not affect the lawfulness of processing before the withdrawal.',
                  },
                ].map((right, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
                    <h4 className="font-semibold text-foreground mb-2">{right.title}</h4>
                    <p className="text-sm text-muted-foreground">{right.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Sub-processors */}
            <motion.div variants={fadeIn} className="mt-12 p-6 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/50">
              <div className="flex items-start space-x-4">
                <GitBranch className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Sub-processors</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    PlotsAlot uses trusted third-party sub-processors to provide our services. All sub-processors are subject to GDPR-compliant data processing agreements (DPA) that ensure your personal data is protected with the same standards we maintain.
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our sub-processors include cloud infrastructure providers, payment processors, email services, analytics providers, and customer support tools. Each sub-processor is carefully selected and vetted for their commitment to data protection and privacy.
                  </p>
                  <a
                    href="/gdpr-subprocessors"
                    className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                    View our Sub-processors
                    <svg
                      className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Exercising your rights */}
            <motion.div variants={fadeIn} className="mt-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">How to Exercise Your Rights</h2>
              <div className="p-6 rounded-lg bg-green-50/50 dark:bg-green-950/20 border border-green-200/50 dark:border-green-900/50">
                <p className="text-muted-foreground mb-4">
                  To exercise any of your rights under GDPR, please contact us with a clear description of your request:
                </p>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Email:</span> support@slashml.com
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Subject:</span> GDPR Data Subject Rights Request
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Response Time:</span> We aim to respond to all requests within 30 days, as required by law.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Identification:</span> We may need to verify your identity to process your request.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Processed on behalf of customers */}
            <motion.div variants={fadeIn} className="mt-12 p-6 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-900/50">
              <div className="flex items-start space-x-4">
                <AlertCircle className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Data Processed on Behalf of Our Customers</h3>
                  <p className="text-sm text-muted-foreground">
                    If your personal data has been processed through PlotsAlot on behalf of one of our customers, that customer is the data controller. We recommend contacting them directly to exercise your rights. If you contact us, please specify the customer, and we will forward your request to them and support their response in compliance with GDPR requirements.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Data Security */}
            <motion.div variants={fadeIn} className="mt-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">Data Security and Retention</h2>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  We implement comprehensive technical and organizational security measures to protect personal data against unauthorized processing, accidental loss, destruction or damage. These measures include:
                </p>
                <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Regular security assessments and penetration testing</li>
                  <li>Secure deletion procedures</li>
                  <li>Incident response and notification procedures</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by law. Specific retention periods vary depending on the nature of the data and the purpose of processing.
                </p>
              </div>
            </motion.div>

            {/* Complaints */}
            <motion.div variants={fadeIn} className="mt-12 p-6 rounded-lg bg-red-50/50 dark:bg-red-950/20 border border-red-200/50 dark:border-red-900/50">
              <h3 className="text-lg font-semibold text-foreground mb-2">Right to Lodge a Complaint</h3>
              <p className="text-sm text-muted-foreground">
                If you believe we have violated your privacy rights or GDPR requirements, you have the right to lodge a complaint with your local data protection authority or supervisory authority. We encourage you to first contact us to resolve any concerns.
              </p>
            </motion.div>

            {/* Contact and Privacy Policy */}
            <motion.div variants={fadeIn} className="mt-12 text-center">
              <p className="text-muted-foreground mb-6">
                For more information about how we collect, use, and protect your data, please review our{' '}
                <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                  Privacy Policy
                </a>
                .
              </p>
              <p className="text-sm text-muted-foreground">
                For any questions or concerns about GDPR compliance, please contact us at{' '}
                <a href="mailto:support@slashml.com" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                  support@slashml.com
                </a>
              </p>
            </motion.div>
          </motion.div>

          {/* Last updated */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-sm text-muted-foreground">
            <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
